/**
 * One-shot Firestore read of a single ad-creative item document.
 *
 * The HTTP surface has no GET /items/:itemId route, and per-item refresh after
 * a task transition must not refetch the whole 100-item list. This reads the
 * existing `projects/{projectId}/ad_creative_items/{itemId}` document directly
 * (a getDoc, NOT a listener — the app-level background-tasks subscription
 * remains the only realtime source).
 */

import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { AdCreativeItem } from '@segmently/schema-models/ad-creative';
import { isSegmentlyOwnedCreativeItem } from './creativeLocaleProjection';

/** Firestore Timestamps → ISO strings, matching the JSON shape listItems returns. */
function normalizeTimestamps(value: unknown): unknown {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (Array.isArray(value)) return value.map(normalizeTimestamps);
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
      result[key] = normalizeTimestamps(entry);
    }
    return result;
  }
  return value;
}

export async function fetchAdCreativeItem(
  projectId: string,
  itemId: string,
): Promise<AdCreativeItem | null> {
  const firestore = CoreFirebaseService.getInstance().firestore;
  const ref = doc(firestore, `projects/${projectId}/ad_creative_items`, itemId);
  const snapshot = await getDoc(ref);
  if (!snapshot.exists()) return null;
  return parseAdCreativeItemDocument(snapshot.id, snapshot.data());
}

/**
 * Strict-v3 reader boundary shared by every direct document refresh. Old
 * pre-production rows and provider-shaped payloads are ignored before a UI
 * workspace can inspect any authored fields.
 */
export function parseAdCreativeItemDocument(
  documentId: string,
  value: unknown,
): AdCreativeItem | null {
  const data = normalizeTimestamps(value);
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const candidate = { ...(data as Record<string, unknown>), id: documentId };
  return isSegmentlyOwnedCreativeItem(candidate) ? candidate : null;
}
